import { View, Text } from 'react-native'
import { usePathname, useRouter } from 'expo-router'
import { Routes } from '@/src/common/enums/routes'
import { Button } from '@/src/common/components/Button'
import { useMutation } from '@apollo/client'
import { graphql } from '@api/mesa'
import { Loading } from '@/src/common/components/Loading'
import { routeForApplicationSubmissionScreen } from './applicationProcessing'
import { ApplicationSubmissionScreen } from '@api/mesa/graphql'
import { useState, useEffect } from 'react'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useSardine } from '@/src/services/Sardine'
import { CardApplicationWrapper } from './cardApplicationWrapper'
import { typography } from '@/src/common/theme'
import { cardApplicationStyles as commonStyle } from './common'

const CONFIRM_CREDIT_REPORT_UNFROZEN = graphql(`
  mutation ConfirmCreditReportUnfrozen {
    confirmCreditReportUnfrozen
  }
`)

export function ApplicationCreditFrozen() {
  const router = useRouter()
  const logError = useLogError()
  const pathname = usePathname()

  const { updateOptions } = useSardine()

  const [confirmCreditReportUnfrozen, { loading: apolloLoading }] = useMutation(
    CONFIRM_CREDIT_REPORT_UNFROZEN,
    {
      onCompleted({ confirmCreditReportUnfrozen }) {
        void (async () => {
          try {
            /** this shouldn't be possible but just in case, if the backend tells us to stay on the same route, just consider it a signal to go back to the processing screen */
            const applicationSubmissionScreen =
              confirmCreditReportUnfrozen === ApplicationSubmissionScreen.FrozenCredit
                ? ApplicationSubmissionScreen.Processing
                : confirmCreditReportUnfrozen

            if (applicationSubmissionScreen === ApplicationSubmissionScreen.Processing) {
              // if we need to go back to the processing screen, wait for a bit to minimize the risk that the processing screen
              // fetches a FROZEN_CREDIT state too early (this state gets updated asynchronously), in which case the user would
              // come right back to this screen which is quite an unintuitive UX
              await new Promise((resolve) => setTimeout(resolve, 8888))
            }

            routeForApplicationSubmissionScreen({
              applicationSubmissionScreen,
              navigateTo: (route) => {
                if (pathname === route) return
                router.replace(route)
              },
              handleError: () =>
                logError(
                  new Error(
                    `Unknown application submission screen: ${applicationSubmissionScreen}`,
                  ),
                ),
            })
          } finally {
            setLoading(false)
          }
        })()
      },
      onError(error) {
        setLoading(false)
        logError(error)
      },
    },
  )

  const [loading, setLoading] = useState(apolloLoading)

  useEffect(() => {
    void updateOptions({ flow: 'application-credit-frozen' })
  }, [updateOptions])

  return (
    <CardApplicationWrapper>
      <View style={commonStyle.headerText}>
        <Text style={typography['heading/heading2']}>Your Credit is Frozen</Text>
      </View>
      <Text style={commonStyle.instructionText}>
        It would seem as if we were unable to access your credit report. If you happen to have your
        profile frozen with any of the credit reporting bureaus, please unfreeze directly with the
        bureau then return here to proceed with your Mesa card application.
      </Text>
      <View style={{ flex: 1 }} />
      <View style={{ marginBottom: 0, gap: 10 }}>
        <Button
          title="Proceed with Application"
          onPress={() => {
            setLoading(true)
            void confirmCreditReportUnfrozen()
          }}
          disabled={loading}
        >
          {loading ? <Loading size="small" /> : undefined}
        </Button>
        <Button
          title="Return Home"
          buttonStyle={commonStyle.returnHomeButtonStyles}
          titleStyle={commonStyle.returnHomeTitleStyles}
          onPress={() => router.replace(Routes.Main.route)}
        />
      </View>
    </CardApplicationWrapper>
  )
}
